import type {
  Client,
  ClientPlan,
  ClientPlanCode,
  ClientPromptCategory,
  ClientStatus,
  Credit,
  RecurrenceStreamType,
} from '@frontend/api-types';
import { isNil, makeCent, match } from '@frontend/duck-tape';

export const FRICTIONLESS_SIGNUP_TAG = 'experiment_no_payment_signup_v1';

export const isFrictionlessSignupUser = (client: MaybeUndefined<Client>): boolean =>
  Boolean(client?.tags?.includes(FRICTIONLESS_SIGNUP_TAG));

export const getInitials = ({ firstName, lastName, nickname }: Pick<Client, 'firstName' | 'lastName' | 'nickname'>) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const firstLetter = nickname?.[0] ?? firstName?.[0] ?? '';
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const lastLetter = lastName?.[0] ?? '';
  return `${firstLetter.toUpperCase()}${lastLetter.toUpperCase()}`.trim();
};

export const getClientDisplayFullName = ({
  firstName,
  lastName,
  nickname,
}: Pick<Client, 'firstName' | 'lastName' | 'nickname'>) =>
  `${firstName} ${lastName} ${nickname ? `(${nickname})` : ''}`;

/**
 * Shown to members as options to choose from
 * NOTE: 'core' is just a rename from 'individual'
 */
export type MarketedMembershipPlanType = 'core' | 'essentials' | 'household' | 'householdPlus' | 'starter';
/**
 * Adds hidden free plan to allow downgrading to the free tier
 */
export type SwitchableMembershipPlanType = HiddenMembershipPlanType | MarketedMembershipPlanType;
/**
 * Note: This is the order of the plans in the UI.
 * These are the standard plans surfaced to members outside of the LinkedIn promo flow.
 */
export const MARKETED_MEMBERSHIP_PLAN_TYPES = [
  'essentials',
  'core',
  'household',
] as const satisfies MarketedMembershipPlanType[];

/**
 * LinkedIn promo members can select from the standard marketed plans plus the starter tier.
 */
export const LINKEDIN_MARKETED_MEMBERSHIP_PLAN_TYPES = [
  'starter',
  ...MARKETED_MEMBERSHIP_PLAN_TYPES,
] as const satisfies MarketedMembershipPlanType[];

const HOUSEHOLD_PLAN_CODES: ClientPlanCode[] = ['HOUSEHOLD', 'HOUSEHOLD_PLUS', 'VIP'];

export const isHousehold = (planCode: ClientPlanCode | undefined) =>
  planCode ? HOUSEHOLD_PLAN_CODES.includes(planCode) : false;

export type MembershipPlanShape<T extends ClientPlanCode = ClientPlanCode> = {
  creditsPerMonth?: number;
  discountDurationMonths?: number;
  discountPrice?: Cent;
  estimatedTasksPerMonth: {
    max: number;
    min: number;
  };
  label: string;
  maxAccounts: number;
  planCode: T;
  price: Cent;
};

export type RetrievePlanTiersArg =
  | {
      promoCode: string;
    }
  | undefined;

/** @deprecated */
type DeprecatedMembershipPlanType = 'vip';

const DEPRECATED_MEMBERSHIP_PLANS = {
  vip: {
    creditsPerMonth: 70,
    estimatedTasksPerMonth: {
      max: 12,
      min: 8,
    },
    label: 'VIP',
    maxAccounts: 2,
    planCode: 'VIP',
    price: makeCent(44900),
  },
} as const satisfies { [key in DeprecatedMembershipPlanType]: MembershipPlanShape };

/** Only for Duckbill employees */
type InternalMembershipPlanType = 'champagneHousehold' | 'champagneIndividual' | 'trial';

const INTERNAL_MEMBERSHIP_PLANS = {
  champagneHousehold: {
    creditsPerMonth: 70,
    estimatedTasksPerMonth: {
      max: 0,
      min: 0,
    },
    label: 'Duckbill Champagne Tester (Household)',
    maxAccounts: 2,
    planCode: 'HOUSEHOLD',
    price: makeCent(0),
  },
  champagneIndividual: {
    creditsPerMonth: 40,
    estimatedTasksPerMonth: {
      max: 0,
      min: 0,
    },
    label: 'Duckbill Champagne Tester (Individual)',
    maxAccounts: 1,
    planCode: 'INDIVIDUAL',
    price: makeCent(50),
  },
  trial: {
    creditsPerMonth: 0,
    estimatedTasksPerMonth: {
      max: 0,
      min: 0,
    },
    label: 'Trial',
    maxAccounts: 1,
    planCode: 'TRIAL',
    price: makeCent(0),
  },
} as const satisfies { [key in InternalMembershipPlanType]: MembershipPlanShape };

const INDIVIDUAL_MEMBERSHIP_PLAN = {
  creditsPerMonth: 40,
  estimatedTasksPerMonth: {
    max: 6,
    min: 4,
  },
  label: 'Core',
  maxAccounts: 1,
  planCode: 'INDIVIDUAL',
  price: makeCent(9900),
} as const satisfies MembershipPlanShape;

export const MARKETED_MEMBERSHIP_PLANS = {
  /** NOTE: 'core' is just a rename from 'individual' */
  core: INDIVIDUAL_MEMBERSHIP_PLAN,
  essentials: {
    creditsPerMonth: 20,
    estimatedTasksPerMonth: {
      max: 4,
      min: 2,
    },
    label: 'Essential',
    maxAccounts: 1,
    planCode: 'ESSENTIAL',
    price: makeCent(4900),
  },
  household: {
    creditsPerMonth: 70,
    estimatedTasksPerMonth: {
      max: 12,
      min: 8,
    },
    label: 'Household',
    maxAccounts: 2,
    planCode: 'HOUSEHOLD',
    price: makeCent(16900),
  },
  householdPlus: {
    creditsPerMonth: 150,
    estimatedTasksPerMonth: {
      max: 24,
      min: 16,
    },
    label: 'Household Plus',
    maxAccounts: 2,
    planCode: 'HOUSEHOLD_PLUS',
    price: makeCent(35000),
  },
  starter: {
    creditsPerMonth: 10,
    estimatedTasksPerMonth: {
      max: 2,
      min: 1,
    },
    label: 'Starter',
    maxAccounts: 1,
    planCode: 'STARTER',
    price: makeCent(2900),
  },
} as const satisfies { [key in MarketedMembershipPlanType]: MembershipPlanShape };

export type MarketedMembershipPlan = (typeof MARKETED_MEMBERSHIP_PLANS)[MarketedMembershipPlanType];

type HiddenMembershipPlanType = 'aLaCarte';

export const HIDDEN_MEMBERSHIP_PLANS = {
  aLaCarte: {
    creditsPerMonth: 10,
    estimatedTasksPerMonth: {
      max: 2,
      min: 1,
    },
    label: 'A La Carte',
    maxAccounts: 1,
    planCode: 'A_LA_CARTE',
    price: makeCent(1000),
  },
} as const satisfies { [key in HiddenMembershipPlanType]: MembershipPlanShape };

export type AllMembershipPlanType =
  | DeprecatedMembershipPlanType
  | HiddenMembershipPlanType
  | InternalMembershipPlanType
  | MarketedMembershipPlanType;

/** Shown to members as options to choose from */
export const ALL_MEMBERSHIP_PLANS = {
  ...DEPRECATED_MEMBERSHIP_PLANS,
  ...INTERNAL_MEMBERSHIP_PLANS,
  ...MARKETED_MEMBERSHIP_PLANS,
  ...HIDDEN_MEMBERSHIP_PLANS,
} as const satisfies {
  [key in AllMembershipPlanType]: MembershipPlanShape;
};

type MembershipPlan = Client | { isEmployeePrice: boolean; plan: ClientPlan };
export const getMembershipPlanType = ({ isEmployeePrice, plan }: MembershipPlan) =>
  plan
    ? match<ClientPlanCode, AllMembershipPlanType>(plan.code)
        .with('ESSENTIAL', () => 'essentials')
        .with('HOUSEHOLD', () => (isEmployeePrice ? 'champagneHousehold' : 'household'))
        .with('HOUSEHOLD_PLUS', () => 'householdPlus')
        .with('INDIVIDUAL', () => 'core')
        .with('VIP', () => 'vip')
        .with('STARTER', () => 'starter')
        .with('TRIAL', () => 'trial')
        .with('A_LA_CARTE', () => 'aLaCarte')
        .exhaustive()
    : undefined;

export const isClientSubscribed = (clientStatus: MaybeUndefined<ClientStatus>): boolean =>
  Boolean(
    clientStatus?.membershipStatus && ['active', 'subscription_pending'].includes(clientStatus?.membershipStatus),
  );

export const isClientOnALaCartePlan = (client: MaybeUndefined<Client>): boolean => client?.plan?.code === 'A_LA_CARTE';

export const getAvailableUpgradeSubscriptionPlans = (
  client: MaybeUndefined<Client>,
  {
    canUpgrade = isClientOnALaCartePlan,
    upgradePlans = () => MARKETED_MEMBERSHIP_PLAN_TYPES,
  }: {
    canUpgrade?: (client: MaybeUndefined<Client>) => boolean;
    upgradePlans?: (client: MaybeUndefined<Client>) => readonly MarketedMembershipPlanType[];
  } = {},
): readonly MarketedMembershipPlanType[] => {
  return canUpgrade(client) ? upgradePlans(client) : [];
};

/**
 * A map of available plans constrained to known membership plan keys.
 *
 * Allows any subset of `ALL_MEMBERSHIP_PLANS` keys while enforcing that values
 * conform to `MembershipPlanShape`.
 *
 * Example:
 * const plans: AvailablePlans = {
 *   core: ALL_MEMBERSHIP_PLANS.core,
 *   essentials: { ...customEssentials },
 * };
 */
export type AvailablePlans = Partial<Record<AllMembershipPlanType, MembershipPlanShape>>;

/**
 * Resolves a normalized membership plan definition for a given client or plan.
 *
 * Given a `MembershipPlan` (either a `Client` or an object `{ isEmployeePrice, plan }`),
 * this helper will:
 * - Determine the canonical plan key via `membershipPlanType` (defaults to `getMembershipPlanType`).
 * - Look up the corresponding plan details in `availablePlans` (defaults to `ALL_MEMBERSHIP_PLANS`).
 *
 * If the input has no plan, or the plan cannot be mapped to a known type, the function
 * returns `undefined`.
 *
 * @param plan Source used to determine the membership plan (client or explicit plan+flag).
 * @param options.availablePlans Optional `AvailablePlans` map of plan definitions to resolve against. Defaults to `ALL_MEMBERSHIP_PLANS`.
 * @param options.membershipPlanType Optional mapper from `MembershipPlan` to `AllMembershipPlanType`. Defaults to `getMembershipPlanType`.
 * @returns The matching `MembershipPlanShape` from `availablePlans`, or `undefined` if none.
 */
type GetMembershipPlanOptions = {
  availablePlans: AvailablePlans;
  membershipPlanType: (plan: MembershipPlan) => AllMembershipPlanType | undefined;
};

export const getMembershipPlan = (
  plan: MembershipPlan,
  { availablePlans, membershipPlanType = getMembershipPlanType }: Partial<GetMembershipPlanOptions> = {},
) => {
  const planType = plan ? membershipPlanType(plan) : undefined;
  if (!planType || !availablePlans?.[planType]) {
    return undefined;
  }
  return availablePlans[planType];
};

export const isDowngradedMembership = (
  currentPlan: AllMembershipPlanType,
  comparingPlan: AllMembershipPlanType,
  { membershipPlans = ALL_MEMBERSHIP_PLANS }: Partial<{ membershipPlans: AvailablePlans }> = {},
): boolean => {
  const currentMembership = membershipPlans[currentPlan];
  const comparingMembership = membershipPlans[comparingPlan];
  if (currentMembership && comparingMembership) {
    return currentMembership.price > comparingMembership.price;
  }
  return false;
};

export const isClientPayingSubscriber = ({
  client,
  clientStatus,
}: {
  client: MaybeUndefined<Client>;
  clientStatus: MaybeUndefined<ClientStatus>;
}): boolean => isClientSubscribed(clientStatus) && !isClientOnALaCartePlan(client);

export const mapRecurrenceStreamTypeToClientPromptCategory = (type: RecurrenceStreamType) =>
  match<RecurrenceStreamType, ClientPromptCategory>(type)
    .with('birthday', () => 'birthday_recurrence')
    .with('maintenance', () => 'maintenance_recurrence')
    .with('medical', () => 'medical_recurrence')
    .with('self_care', () => 'self_care_recurrence')
    .exhaustive();

export const mapClientPromptCategoryToRecurrenceStreamType = (category: ClientPromptCategory) =>
  match<ClientPromptCategory, RecurrenceStreamType>(category)
    .with('birthday_recurrence', () => 'birthday')
    .with('maintenance_recurrence', () => 'maintenance')
    .with('medical_recurrence', () => 'medical')
    .with('self_care_recurrence', () => 'self_care')
    .exhaustive();

export const getReferralUrlWithPromo = (
  referralUrl: string | undefined,
  { promoCode = 'DBREFERRAL' }: { promoCode?: string } = {},
): string => {
  if (!referralUrl) return '';
  return `${referralUrl}?promo=${promoCode}`;
};

export const hasPaymentMethod = (clientStatus: MaybeUndefined<ClientStatus>): clientStatus is ClientStatus =>
  Boolean(clientStatus?.hasPaymentMethod);

export const hasCompletedOnboarding = (clientStatus: MaybeUndefined<ClientStatus>): clientStatus is ClientStatus =>
  Boolean(clientStatus?.onboardingComplete);

export const hasCredits = (credits: MaybeUndefined<Credit>): credits is Credit =>
  !isNil(credits?.availableCredits) && credits?.availableCredits > 0;

export const canBuyCredits = (obj: {
  client?: MaybeUndefined<Client>;
  clientStatus: MaybeUndefined<ClientStatus>;
  credits: MaybeUndefined<Credit>;
}): obj is { clientStatus: ClientStatus; credits: Credit } =>
  !isFrictionlessSignupUser(obj.client) && hasPaymentMethod(obj.clientStatus) && !isNil(obj.credits?.availableCredits);

/** To check if user has enough credits to authorize/start human work */
export const hasSufficientCredits = ({
  credits,
  creditsCost,
}: {
  credits: MaybeUndefined<Credit>;
  creditsCost: number;
}): boolean => hasCredits(credits) && credits.availableCredits >= creditsCost;

/**
 * Determines if the paywall modal should be displayed on the home screen
 *
 * Shows paywall when:
 * - User is a household owner (can manage subscription)
 * - Has NOT completed onboarding (still in setup flow)
 * - Has NO payment method (needs to add payment)
 * - Is in an active or pending subscription state (eligible for payment setup)
 *
 * @returns true if paywall should be shown, false otherwise
 */
export const shouldDisplayPaywallOnHomeScreen = (
  client: MaybeUndefined<Client>,
  clientStatus: MaybeUndefined<ClientStatus>,
): boolean => {
  if (!clientStatus || !client) {
    return false;
  }

  // Frictionless signup users skip onboarding paywall entirely
  if (isFrictionlessSignupUser(client)) {
    return false;
  }

  // Don't show paywall if not in an active subscription state (inactive, canceled, etc.)
  if (clientStatus.membershipStatus && !isClientSubscribed(clientStatus)) {
    return false;
  }

  // Show paywall modal if household owner, onboarding incomplete, and no payment method
  return !!client.isHouseholdOwner && !hasCompletedOnboarding(clientStatus) && !hasPaymentMethod(clientStatus);
};

export const getMembershipPlanDisplayPricing = (
  plan: MaybeUndefined<{ discountDurationMonths?: number; discountPrice?: Cent; price: Cent }>,
) => {
  if (!plan) {
    return undefined;
  }

  const hasDiscount = typeof plan.discountPrice !== 'undefined' && plan.discountPrice < plan.price;

  return {
    discountDurationMonths: hasDiscount ? plan.discountDurationMonths : undefined,
    hasDiscount,
    primaryPrice: hasDiscount ? plan.discountPrice : plan.price,
    secondaryPrice: hasDiscount ? plan.price : undefined,
  };
};

export const getAvailableSubscriptionPlans = () => {
  return MARKETED_MEMBERSHIP_PLAN_TYPES;
};

/**
 * Task estimation constants derived from plan data.
 * Based on historical plan ratios showing ~6-10 credits per task.
 *
 * @see MARKETED_MEMBERSHIP_PLANS for the source data:
 * - 10 credits → 1-2 tasks
 * - 20 credits → 2-4 tasks
 * - 40 credits → 4-6 tasks
 * - 70 credits → 8-12 tasks
 * - 150 credits → 16-24 tasks
 */
export const CREDITS_PER_TASK_MAX = 10;
export const CREDITS_PER_TASK_MIN = 6;

/**
 * Estimate task range from credit amount.
 * Uses the same ratios as defined in MARKETED_MEMBERSHIP_PLANS.
 * When min equals max, callers handle the singular display (e.g. "~1 task").
 */
export const getEstimatedTasksFromCredits = (credits: number): { max: number; min: number } => {
  const min = Math.max(1, Math.floor(credits / CREDITS_PER_TASK_MAX));
  const max = Math.max(1, Math.ceil(credits / CREDITS_PER_TASK_MIN));

  return { max, min };
};
